import Head from 'next/head';
import { useRouter } from 'next/router';
import { gql } from '@apollo/client';
import { useQuery } from '@helpers/async';
import { initializeApollo, addApolloState } from '../../apollo-client';
import NextNprogress from 'nextjs-progressbar';

import Layout from '../../components/Layout';
import { Container, Box, Heading, Link, Button, ArrowLeftIcon } from '@terminal/design-system';

import metaRobots from '../../helpers/metaRobots';

import sharecardImage from '../../public/images/sharecard.jpg';
import GutenbergStylesheet from '../../styles/gutenberg';
import styles from '../../styles/legal/content.module.scss';
import sitePath from '../../helpers/sitePath';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { filterSinglePostByCategory } from '@helpers/categoryFilters';

const footer = {
  longText: false,
  text: 'Find the scale you need. Build your team today.',
};

const GET_PRESSRELEASE = gql`
  query GetPressRelease($slug: ID!) {
    pressRelease(id: $slug, idType: SLUG) {
      id
      date
      title
      seo {
        title
        metaDesc
        metaRobotsNofollow
        metaRobotsNoindex
      }
      content
    }
  }
`;

export async function getServerSideProps({ query, locale }) {
  const { slug } = query;

  const apolloClient = initializeApollo();

  await apolloClient.query({
    query: GET_PRESSRELEASE,
    variables: {
      slug,
    },
  });

  return addApolloState(apolloClient, {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  });
}

export default function PressRelease() {
  const router = useRouter();

  const { slug } = router.query;

  // Client side Apollo
  const { loading, data } = useQuery(GET_PRESSRELEASE, {
    variables: {
      slug,
    },
  });

  // CSR loading
  if (loading) {
    return (
      <NextNprogress
        color="#25383F"
        startPosition={0.3}
        stopDelayMs={200}
        height={3}
        showOnShallow={true}
        options={{
          showSpinner: false,
        }}
      />
    );
  }
  const title = data?.pressRelease?.title ?? null;
  const date = data?.pressRelease?.date ?? null;
  const content = data?.pressRelease?.content ?? null;

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-ca', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const meta = {
    title: data?.pressRelease?.seo.title ?? null,
    description: data?.pressRelease?.seo.metaDesc ?? null,
  };

  return (
    <>
      <Head>
        <link rel="canonical" href={sitePath(router.asPath)} />
        <title>{meta.title}</title>
        <meta property="og:title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={sitePath(router.asPath)} />
        <meta property="og:image" content={sharecardImage.src} />
        <meta name="robots" content={metaRobots({ translated: false, locale: router.locale })} />
      </Head>
      <GutenbergStylesheet />
      <Layout type="businesses" footer={footer} withCTA>
        <Container mt={{ base: '6', lg: '8' }}>
          <Box py={3}>
            <Button
              as={Link}
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeftIcon />}
              href="/resources"
            >
              Go back to Resources
            </Button>
          </Box>
          <Box mb="4">
            <Link
              href="/press"
              display="inline"
              fontWeight="semibold"
              _hover={{ color: 'green.400', textDecoration: 'underline' }}
              _focus={{ color: 'green.400', textDecoration: 'underline' }}
            >
              Press
            </Link>
            <Box display="inline">&nbsp;&rsaquo; {title}</Box>
          </Box>
          <Heading
            as="h1"
            fontSize={{ base: '2.5rem', lg: '3rem' }}
            lineHeight={{ base: '2.75rem', lg: '3.5rem' }}
          >
            {title}
          </Heading>
          <Box mb={{ base: '4', lg: '6' }}>{formatDate(date)}</Box>
          <Box
            className={styles.content}
            dangerouslySetInnerHTML={{
              __html: content,
            }}
          />
        </Container>
      </Layout>
    </>
  );
}
